/* eslint-disable react-hooks/rules-of-hooks */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import * as d3 from 'd3';
import Color from 'color';
import { contextMenu } from 'react-contexify';

import { getChartFontColor, getChartGridColor } from '../../../../utils/chart';
import { VIEWS } from '../../../../constants';
import { getTipCoordinates } from '../d3Util';
import AnimatedText from './AnimatedText';
import AnimatedCircle from './AnimatedCircle';
import {
  CLUSTER_OPTIONS,
  bubbleFontSize,
  bubbleTextColor,
  clusterData,
  createTooltipContent,
  mapPropsToDto,
  truncateTextLabel,
} from './utils';
import useZoom from './useZoom';
import Toolbar from '../toolbar/Toolbar';
import VisitWebsiteContextMenu from '../../../visit-website-context-menu/VisitWebsiteContextMenu';
import NoChartsData from '../../no-charts-data/NoChartsData';

import scss from './BubblesChart.module.scss';

const wrapperHeight = 800;

const { GROUPS } = VIEWS;

export const BUBBLES_CONTEXT_MENU = 'bubbles/VisitWebsiteContextMenu';

const BubblesChart = ({ data: dataPros, dataKey, reverseTrendGroup, view, forceClusterByGroup }) => {
  if (!dataPros.length) {
    return <NoChartsData />;
  }

  const theme = useSelector((state) => state.global.theme);

  const wrapperRef = useRef(null);

  const gridColor = getChartGridColor(theme);
  const labelsColor = getChartFontColor(theme);

  const [clusterBy, setClusterBy] = useState([]);
  const [width, setChartWidth] = useState(0);

  const { rootGRef, svgRef, zoomIn, resetZoom, zoomOut, initializeZoom, setupZoom } = useZoom();

  const dtoList = useMemo(
    () =>
      mapPropsToDto({
        data: dataPros,
        dataKey,
        isReversedTrend: reverseTrendGroup,
        theme,
      }),
    [dataPros, reverseTrendGroup, theme, dataKey],
  );
  const data = useMemo(() => clusterData({ dtoList, clusterBy }), [dtoList, clusterBy]);

  const hierarchy = useMemo(() => d3.hierarchy(data).sum((d) => d.score), [data]);
  const root = useMemo(() => {
    return d3
      .pack()
      .size([width || 500, wrapperHeight])
      .padding(3)(hierarchy);
  }, [hierarchy, width]);

  const handleChangeClusterBy = useCallback((clusterOption) => {
    setClusterBy((prevState) =>
      prevState.includes(clusterOption)
        ? prevState.filter((option) => option !== clusterOption)
        : [...prevState, clusterOption],
    );
  }, []);

  const setupWidth = useCallback(() => {
    const width = wrapperRef.current.clientWidth;

    setChartWidth(width);
  }, []);

  const setupTooltip = useCallback(() => {
    const points = d3.select(svgRef.current).selectAll('.node');

    const tip = d3
      .select(wrapperRef.current)
      .append('div')
      .attr('class', scss.tooltip)
      .style('position', 'fixed')
      .style('opacity', 0);

    points
      .on('mouseover', (event) => {
        const content = createTooltipContent(event, dtoList);
        tip.html(content);

        const place = getTipCoordinates(event, tip.node(), wrapperRef.current);
        tip
          .transition()
          .duration(200)
          .style('opacity', 0.9)
          .style('left', place.x + 'px')
          .style('top', place.y + 'px');
      })
      .on('mouseout', () => {
        tip.transition().duration(500).style('opacity', 0);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dtoList]);

  const allClusters = useMemo(() => {
    const nodes = Array.from({ length: clusterBy.length }, (_, index) => index + 1).flatMap((depth) =>
      root.descendants().filter((node) => node.depth === depth),
    );

    return nodes.map((node, index) => (
      <g key={node.data.clusterValue ? `${node.data.clusterValue}-${index}` : index + 1}>
        <circle cx={node.x} cy={node.y} r={node.r} stroke={gridColor} strokeWidth={0.5} fill={'transparent'} />
      </g>
    ));
  }, [clusterBy, gridColor, root]);

  const allLeafCircles = root.leaves().map((leaf) => {
    const domain = leaf.data.domain;
    const label = leaf.data.label;

    if (!domain) {
      return;
    }

    const fontSize = bubbleFontSize({
      isClusterLabel: leaf.data.isClusterLabel,
      r: leaf.r,
    });
    const textColor = bubbleTextColor({
      isClusterLabel: leaf.data.isClusterLabel,
      inFocus: leaf.data.inFocus,
      myCompany: leaf.data.myCompany,
      labelsColor,
    });

    const displayContextMenu = (event) => {
      contextMenu.show({
        id: BUBBLES_CONTEXT_MENU,
        event,
        props: {
          domain,
        },
      });
    };

    return (
      <g
        key={`${domain}-${leaf.data.score}-${label}`}
        className={leaf.data.isClusterLabel ? '' : 'node'}
        onContextMenu={displayContextMenu}>
        <AnimatedCircle
          cx={leaf.x}
          cy={leaf.y}
          r={leaf.r}
          cursor={'pointer'}
          fill={
            leaf.data.isClusterLabel
              ? 'transparent'
              : !leaf.data.inFocus || !leaf.data.myCompany
              ? leaf.data.color
              : Color(leaf.data.color).lighten(0.1).hex()
          }
          data-id={domain}
        />

        <AnimatedText
          x={leaf.x}
          y={leaf.y}
          dy='.2em'
          textAnchor='middle'
          cursor={'pointer'}
          fontFamily='Inter'
          fill={textColor}
          fontSize={fontSize}
          data-id={domain}>
          {leaf.data.isClusterLabel
            ? domain
            : truncateTextLabel({
                r: leaf.r,
                title: label || domain,
                fontSize,
              })}
        </AnimatedText>
      </g>
    );
  });

  useEffect(() => {
    setupWidth();

    if (width) {
      initializeZoom();
      setupZoom();
      setupTooltip();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dtoList, width]);

  useEffect(() => {
    if (view === GROUPS && !forceClusterByGroup && clusterBy.includes(CLUSTER_OPTIONS.GROUP)) {
      setClusterBy((prevState) => prevState.filter((options) => options !== CLUSTER_OPTIONS.GROUP));
    }

    if (view === GROUPS && forceClusterByGroup && !clusterBy.includes(CLUSTER_OPTIONS.GROUP)) {
      handleChangeClusterBy(CLUSTER_OPTIONS.GROUP);
    }
  }, [view, forceClusterByGroup, clusterBy, handleChangeClusterBy]);

  // RERENDER CHART ON RESIZE
  useEffect(() => {
    window.addEventListener('resize', setupWidth);

    return () => {
      window.removeEventListener('resize', setupWidth);
    };
  }, [setupWidth]);

  return (
    <div className={scss.chart}>
      <Toolbar
        extraClass={scss.toolbar}
        buttons={[
          { text: 'reset', handler: resetZoom },
          { text: 'zoomIn', handler: zoomIn },
          { text: 'zoomOut', handler: zoomOut },
          {
            text: clusterBy.includes(CLUSTER_OPTIONS.TREND) ? 'unclusterByTrend' : 'clusterByTrend',
            handler: () => handleChangeClusterBy(CLUSTER_OPTIONS.TREND),
          },
          {
            text: clusterBy.includes(CLUSTER_OPTIONS.GROUP) ? 'unclusterByGroup' : 'clusterByGroup',
            handler: () => handleChangeClusterBy(CLUSTER_OPTIONS.GROUP),
            disabled: view === GROUPS,
          },
        ]}
      />
      <div>
        <div
          ref={wrapperRef}
          style={{
            height: `${wrapperHeight}px`,
            display: 'flex',
            justifyContent: 'center',
          }}>
          <svg ref={svgRef} width={width} height={wrapperHeight} style={{ display: 'inline-block' }}>
            <g ref={rootGRef}>
              {allClusters}
              {allLeafCircles}
            </g>
          </svg>
        </div>
      </div>

      <VisitWebsiteContextMenu id={BUBBLES_CONTEXT_MENU} />
    </div>
  );
};

export default BubblesChart;
